import React, { type ReactNode } from 'react';
import { Pressable, StyleSheet, Text, View, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { goBackOrLanding } from '../../core/navigation/navigationRef';
import { colors, spacing, typography } from '../../core/theme/tokens';

type MarketingBackBarProps = {
  title: string;
  right?: ReactNode;
};

/**
 * Native-style top bar for landing subpages (Privacy, Terms, Support, Explore).
 * Back is on the left, below the status bar — not a web pill under the notch.
 */
export function MarketingBackBar({ title, right }: MarketingBackBarProps) {
  const insets = useSafeAreaInsets();

  return (
    <View style={[styles.bar, { paddingTop: Math.max(insets.top, 8) }]}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Back"
        onPress={goBackOrLanding}
        hitSlop={8}
        style={({ pressed }) => [styles.back, pressed && styles.pressed]}
      >
        <Text style={styles.chevron}>‹</Text>
        <Text style={styles.backText}>Back</Text>
      </Pressable>
      <Text style={styles.title} numberOfLines={1}>
        {title}
      </Text>
      <View style={styles.right}>{right ?? <View style={styles.rightSpacer} />}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingBottom: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
    backgroundColor: colors.background,
  },
  back: {
    minWidth: 72,
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingRight: spacing.sm,
  } as ViewStyle,
  chevron: {
    fontSize: 28,
    lineHeight: 32,
    fontWeight: '400',
    color: colors.accent,
    marginRight: 2,
    marginTop: -2,
  },
  backText: {
    ...typography.body,
    fontSize: 17,
    fontWeight: '600',
    color: colors.accent,
  },
  title: {
    ...typography.headline,
    fontSize: 17,
    color: colors.text,
    flex: 1,
    textAlign: 'center',
  },
  right: {
    minWidth: 72,
    minHeight: 44,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  rightSpacer: {
    width: 72,
    height: 44,
  },
  pressed: {
    opacity: 0.65,
  },
});
